import { Box, Flex, Text } from '@pancakeswap/uikit'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

/** CADINU look for the competition pages: navy surfaces, hairline borders, brand gradient only on the one primary CTA. */
export const DISPLAY = "'Gabarito','Gabarito Fallback',system-ui,sans-serif"
export const MONO = "'Martian Mono','Martian Mono Fallback',ui-monospace,monospace"

export const surface = css`
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 20px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 10px 30px rgba(0, 0, 0, 0.35);
  min-width: 0;
`
export const Card = styled(Box)`
  ${surface}
  padding: 16px;
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 20px;
  }
`
export const Display = styled(Text)`
  font-family: ${DISPLAY};
  letter-spacing: -0.01em;
`
export const Num = styled.span`
  font-family: ${MONO};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
  letter-spacing: -0.02em;
`
export const Eyebrow = styled(Text)`
  font-family: ${MONO};
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSubtle};
`
export const Addr = styled.span`
  font-family: ${MONO};
  font-size: 0.92em;
  overflow-wrap: anywhere;
  word-break: break-word;
`

export const PrimaryCta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 52px;
  padding: 0 24px;
  border-radius: 16px;
  border: 0;
  cursor: pointer;
  font-family: ${DISPLAY};
  font-weight: 800;
  font-size: 17px;
  color: #f4f6ff;
  background: ${({ theme }) => theme.colors.gradientBold};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 8px 24px rgba(33, 0, 255, 0.35);
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 12px 30px rgba(126, 0, 254, 0.45);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 3px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }
`
export const GhostCta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 0 20px;
  border-radius: 16px;
  cursor: pointer;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background: transparent;
  text-decoration: none;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

export const Chip = styled.button<{ $on?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 0 16px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  white-space: nowrap;
  color: ${({ theme, $on }) => ($on ? theme.colors.textOnFill ?? '#050713' : theme.colors.text)};
  background: ${({ theme, $on }) => ($on ? theme.colors.primary : 'transparent')};
  border: 1px solid ${({ theme, $on }) => ($on ? theme.colors.primary : theme.colors.cardBorder)};
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(57, 217, 138, 0.55); }
  100% { box-shadow: 0 0 0 8px rgba(57, 217, 138, 0); }
`
type Tone = 'success' | 'info' | 'warning' | 'muted' | 'danger'
const toneColor = (theme: any, tone: Tone) =>
  ({ success: theme.colors.success, info: theme.colors.primary, warning: theme.colors.warning, danger: theme.colors.failure, muted: theme.colors.textSubtle })[tone]
export const Pill = styled.span<{ $tone: Tone; $live?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  white-space: nowrap;
  color: ${({ theme, $tone }) => toneColor(theme, $tone)};
  border: 1px solid ${({ theme, $tone }) => toneColor(theme, $tone)}77;
  background: ${({ theme, $tone }) => toneColor(theme, $tone)}26;
  ${({ $live }) =>
    $live &&
    css`
      &::before {
        content: '';
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: currentColor;
        animation: ${pulse} 1.6s ease-out infinite;
      }
      @media (prefers-reduced-motion: reduce) {
        &::before {
          animation: none;
        }
      }
    `}
`

export const PHASE: Record<string, { label: string; tone: Tone }> = {
  live: { label: 'Live', tone: 'success' },
  upcoming: { label: 'Upcoming', tone: 'info' },
  awaiting_result: { label: 'Awaiting result', tone: 'warning' },
  refundable: { label: 'Refund available', tone: 'warning' },
  settled: { label: 'Finished', tone: 'muted' },
  refunded: { label: 'Refunded', tone: 'muted' },
}
export function PhasePill({ phase }: { phase: string }) {
  const p = PHASE[phase] ?? { label: phase, tone: 'muted' as Tone }
  return (
    <Pill $tone={p.tone} $live={phase === 'live'}>
      {p.label}
    </Pill>
  )
}

export const Bar = styled.div<{ $pct: number; $tone?: Tone }>`
  height: 6px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.tertiary};
  overflow: hidden;
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ $pct }) => Math.max(0, Math.min(100, $pct))}%;
    border-radius: 999px;
    background: ${({ theme, $tone = 'info' }) => toneColor(theme, $tone)};
  }
`

/** Seconds since epoch, ticking; null before hydration so server and client markup agree. */
export function useNow(intervalMs = 1000) {
  const [now, setNow] = useState<number | null>(null)
  useEffect(() => {
    setNow(Math.floor(Date.now() / 1000))
    const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), intervalMs)
    return () => clearInterval(t)
  }, [intervalMs])
  return now
}

export const fmtDuration = (sec: number) => {
  const s = Math.max(0, Math.floor(sec))
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  const p = (n: number) => String(n).padStart(2, '0')
  if (d > 0) return `${d}d ${p(h)}h ${p(m)}m`
  return `${p(h)}:${p(m)}:${p(s % 60)}`
}
export const fmtDate = (t: number) => `${new Date(t * 1000).toISOString().replace('T', ' ').slice(0, 16)} UTC`

/** Live: time left. Upcoming: time to start. Otherwise the end date. */
export function Countdown({ c }: { c: { phase: string; startTime: number; endTime: number; refundAfter?: number } }) {
  const now = useNow()
  if (now === null) return <Num>—</Num>
  if (c.phase === 'live') return <Num>Ends in {fmtDuration(c.endTime - now)}</Num>
  if (c.phase === 'upcoming') return <Num>Starts in {fmtDuration(c.startTime - now)}</Num>
  if (c.phase === 'awaiting_result') return <Num>Result due in {fmtDuration((c.refundAfter ?? c.endTime) - now)}</Num>
  return <Num>Ended {fmtDate(c.endTime)}</Num>
}

export const Icon3d = ({ name, size = 32 }: { name: string; size?: number }) => (
  <Image src={`/images/brand/icons3d/${name}.png`} alt="" width={size} height={size} unoptimized style={{ flex: 'none' }} />
)

/** Fluent Emoji 3D (Microsoft, MIT): the icon set shared with TiCkTOM, files only (public/images/fluent). Decorative: the label next to it carries the meaning. */
export const Fluent = ({ name, size = 24, style }: { name: string; size?: number; style?: React.CSSProperties }) => (
  <Image src={`/images/fluent/${name}.png`} alt="" width={size} height={size} unoptimized style={{ flex: 'none', ...style }} />
)

export const Grid = styled(Box)`
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr);
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  ${({ theme }) => theme.mediaQueries.xl} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`
export const Row = styled(Flex)`
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`
